<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { sanitizeHtml, sanitizeResourceUrl } from "@/utils/sanitizeHtml.js";
import {
    isAllowedMuralImage,
    MAX_MURAL_IMAGE_SIZE,
    MURAL_IMAGE_ACCEPT,
    readImageAsDataUrl
} from "@/utils/muralImage.js";

const props = defineProps({
    post: {
        type: Object,
        required: true
    },
    canManage: {
        type: Boolean,
        default: false
    },
    editing: {
        type: Boolean,
        default: false
    },
    saving: {
        type: Boolean,
        default: false
    },
    deleting: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["edit", "cancel-edit", "save", "delete"]);
const { locale, t } = useI18n();
const attachmentLoading = ref(true);
const hasEditorContent = ref(Boolean(props.post.body));
const editAttachment = ref(props.post.anexo || "");
const editAttachmentName = ref("");
const attachmentChanged = ref(false);
const editAttachmentError = ref("");
const editFileInput = ref(null);
const showEmojiMenu = ref(false);
const collaborator = computed(() => props.post.colaborador || {});
const userPost = computed(() => props.post.user || {});
const author = computed(() => userPost.value.name || t("components.rhdigital.mural.posts.unknownAuthor"));
const avatar = computed(() => sanitizeResourceUrl(collaborator.value.foto, { allowDataImage: true }));
const body = computed(() => sanitizeHtml(props.post.body));
const attachment = computed(() => sanitizeResourceUrl(props.post.anexo, { allowDataImage: true }));
const editAttachmentPreview = computed(() => sanitizeResourceUrl(editAttachment.value, { allowDataImage: true }));
const emojiOptions = [
    { name: "grinning", symbol: "😀" },
    { name: "heart_eyes", symbol: "😍" },
    { name: "clap", symbol: "👏" },
    { name: "thumbs_up", symbol: "👍" },
    { name: "tada", symbol: "🎉" },
    { name: "rocket", symbol: "🚀" }
];
const initials = computed(() => author.value
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0))
    .join("")
    .toUpperCase());
const publishedAt = computed(() => {
    const value = props.post.updated_at || props.post.created_at;
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(locale.value, {
        dateStyle: "medium",
        timeStyle: "short"
    }).format(date);
});

const editor = useEditor({
    content: props.post.body || "",
    extensions: [
        StarterKit.configure({ link: false, underline: false }),
        Underline,
        Link.configure({
            autolink: true,
            openOnClick: false,
            HTMLAttributes: {
                rel: "noopener noreferrer",
                target: "_blank"
            }
        })
    ],
    editorProps: {
        attributes: {
            class: "mural-post__edit-content"
        }
    },
    onUpdate: ({ editor }) => {
        hasEditorContent.value = !editor.isEmpty;
    }
});

const canSave = computed(() => {
    return !props.saving && Boolean(hasEditorContent.value || editAttachment.value);
});

watch(() => props.editing, editing => {
    if (editing) {
        editor.value?.commands.setContent(props.post.body || "");
        hasEditorContent.value = !editor.value?.isEmpty;
        editor.value?.setEditable(!props.saving);
        editAttachment.value = props.post.anexo || "";
        editAttachmentName.value = "";
        attachmentChanged.value = false;
        editAttachmentError.value = "";
        showEmojiMenu.value = false;
    }
});

watch(() => props.saving, saving => {
    editor.value?.setEditable(!saving);
});

function savePost() {
    if (!canSave.value) return;

    const payload = { id: props.post.id, body: editor.value?.getHTML() || "" };
    if (attachmentChanged.value) payload.anexo = editAttachment.value || "";

    emit("save", payload);
}

function insertEmoji(emoji) {
    editor.value?.chain().focus().insertContent(emoji.symbol).run();
    showEmojiMenu.value = false;
}

function openEditFileDialog() {
    if (!props.saving) editFileInput.value?.click();
}

async function selectEditAttachment(event) {
    const file = event.target.files?.[0];
    editAttachmentError.value = "";

    if (!file) return;

    if (!isAllowedMuralImage(file)) {
        editAttachmentError.value = t("components.rhdigital.mural.composer.invalidImageType");
        event.target.value = "";
        return;
    }

    if (!file.size) {
        editAttachmentError.value = t("components.rhdigital.mural.composer.emptyFile");
        event.target.value = "";
        return;
    }

    if (file.size > MAX_MURAL_IMAGE_SIZE) {
        editAttachmentError.value = t("components.rhdigital.mural.composer.fileTooLarge", { size: 2 });
        event.target.value = "";
        return;
    }

    try {
        editAttachment.value = await readImageAsDataUrl(file);
        editAttachmentName.value = file.name;
        attachmentChanged.value = true;
    } catch {
        editAttachmentError.value = t("components.rhdigital.mural.composer.imageReadError");
        event.target.value = "";
    }
}

function removeEditAttachment() {
    if (props.saving) return;
    editAttachment.value = "";
    editAttachmentName.value = "";
    attachmentChanged.value = true;
    editAttachmentError.value = "";
    if (editFileInput.value) editFileInput.value.value = "";
}
</script>

<template>
    <article class="mural-post">
        <header class="mural-post__header">
            <div class="mural-post__author">
                <img v-if="avatar" :src="avatar" :alt="author">
                <span v-else class="mural-post__avatar-fallback" aria-hidden="true">{{ initials }}</span>

                <div>
                    <h3>{{ author }}</h3>
                    <span>{{ publishedAt }}</span>
                </div>
            </div>

            <v-menu v-if="canManage && !editing" location="bottom end">
                <template #activator="{ props: menuProps }">
                    <button
                        v-bind="menuProps"
                        class="mural-post__menu"
                        type="button"
                        :disabled="saving || deleting"
                        :aria-label="t('components.rhdigital.mural.posts.moreOptions')"
                    >
                        <v-progress-circular v-if="deleting" indeterminate :size="18" :width="2" />
                        <span v-else class="material-symbols-outlined">more_vert</span>
                    </button>
                </template>

                <v-list class="mural-post__menu-list" density="compact">
                    <v-list-item @click="emit('edit', post)">
                        <template #prepend><span class="material-symbols-outlined">edit</span></template>
                        <v-list-item-title>{{ t("common.labels.edit") }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item class="mural-post__delete-option" @click="emit('delete', post)">
                        <template #prepend><span class="material-symbols-outlined">delete</span></template>
                        <v-list-item-title>{{ t("common.labels.delete") }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </header>

        <div v-if="editing" class="mural-post__edit">
            <div v-if="editor" class="mural-post__edit-toolbar" :aria-label="t('components.rhdigital.mural.composer.tools')">
                <button type="button" :class="{ 'is-active': editor.isActive('bold') }" :title="t('components.rhdigital.mural.composer.bold')" @click="editor.chain().focus().toggleBold().run()">
                    <span class="material-symbols-outlined">format_bold</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('italic') }" :title="t('components.rhdigital.mural.composer.italic')" @click="editor.chain().focus().toggleItalic().run()">
                    <span class="material-symbols-outlined">format_italic</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('underline') }" :title="t('components.rhdigital.mural.composer.underline')" @click="editor.chain().focus().toggleUnderline().run()">
                    <span class="material-symbols-outlined">format_underlined</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('bulletList') }" :title="t('components.rhdigital.mural.composer.bulletList')" @click="editor.chain().focus().toggleBulletList().run()">
                    <span class="material-symbols-outlined">format_list_bulleted</span>
                </button>
                <button type="button" :class="{ 'is-active': editor.isActive('orderedList') }" :title="t('components.rhdigital.mural.composer.orderedList')" @click="editor.chain().focus().toggleOrderedList().run()">
                    <span class="material-symbols-outlined">format_list_numbered</span>
                </button>
                <div class="mural-post__edit-emoji-wrapper">
                    <button type="button" :title="t('components.rhdigital.mural.composer.emoji')" @click="showEmojiMenu = !showEmojiMenu">
                        <span class="material-symbols-outlined">mood</span>
                    </button>
                    <div v-if="showEmojiMenu" class="mural-post__edit-emoji-menu">
                        <button
                            v-for="emoji in emojiOptions"
                            :key="emoji.name"
                            type="button"
                            :title="t(`components.rhdigital.mural.composer.emojiNames.${emoji.name}`)"
                            @click="insertEmoji(emoji)"
                        >
                            {{ emoji.symbol }}
                        </button>
                    </div>
                </div>
            </div>
            <EditorContent :editor="editor" />

            <div v-if="editAttachmentPreview" class="mural-post__edit-attachment">
                <img :src="editAttachmentPreview" :alt="t('components.rhdigital.mural.composer.selectedImageAlt')">
                <div class="mural-post__edit-attachment-actions">
                    <span v-if="editAttachmentName" class="mural-post__edit-attachment-name">{{ editAttachmentName }}</span>
                    <button type="button" :disabled="saving" @click="openEditFileDialog">
                        <span class="material-symbols-outlined">image</span>
                        {{ t("components.rhdigital.mural.posts.replaceImage") }}
                    </button>
                    <button
                        class="mural-post__edit-remove-image"
                        type="button"
                        :disabled="saving"
                        :title="t('components.rhdigital.mural.composer.removeAttachment')"
                        :aria-label="t('components.rhdigital.mural.composer.removeAttachment')"
                        @click="removeEditAttachment"
                    >
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
            </div>

            <button
                v-else
                class="mural-post__edit-add-image"
                type="button"
                :disabled="saving"
                @click="openEditFileDialog"
            >
                <span class="material-symbols-outlined">add_photo_alternate</span>
                {{ t("components.rhdigital.mural.posts.addImage") }}
            </button>

            <input
                ref="editFileInput"
                type="file"
                :accept="MURAL_IMAGE_ACCEPT"
                class="mural-post__edit-file-input"
                @change="selectEditAttachment"
            >
            <p v-if="editAttachmentError" class="mural-post__edit-error">{{ editAttachmentError }}</p>

            <div class="mural-post__edit-actions">
                <button type="button" :disabled="saving" @click="emit('cancel-edit')">
                    {{ t("common.labels.cancel") }}
                </button>
                <button class="mural-post__save" type="button" :disabled="!canSave" @click="savePost">
                    <v-progress-circular v-if="saving" indeterminate :size="17" :width="2" />
                    <span v-else class="material-symbols-outlined">save</span>
                    {{ t("common.labels.save") }}
                </button>
            </div>
        </div>

        <div v-else-if="body" class="mural-post__body" v-html="body"></div>

        <div v-if="attachment && !editing" class="mural-post__attachment">
            <div v-if="attachmentLoading" class="mural-post__attachment-loading">
                <v-progress-circular indeterminate :size="20" :width="2" />
                {{ t("components.rhdigital.mural.posts.loadingPost") }}
            </div>
            <img
                :src="attachment"
                :alt="t('components.rhdigital.mural.posts.attachmentAlt')"
                @load="attachmentLoading = false"
                @error="attachmentLoading = false"
            >
        </div>
    </article>
</template>

<style lang="scss" scoped>
.mural-post {
    padding: 20px;
    border: 1px solid #dde5f3;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 12px 30px rgba(34, 48, 73, 0.04);

    &__header,
    &__author {
        display: flex;
        align-items: center;
    }

    &__header {
        justify-content: space-between;
        gap: 16px;
    }

    &__author {
        gap: 12px;
        min-width: 0;

        img,
        .mural-post__avatar-fallback {
            width: 40px;
            height: 40px;
            flex: 0 0 auto;
            border-radius: 50%;
        }

        img {
            object-fit: cover;
        }

        h3 {
            margin: 0;
            color: #2f3b52;
            font-size: 15px;
            font-weight: 800;
            line-height: 1.2;
        }

        span:not(.mural-post__avatar-fallback) {
            display: block;
            margin-top: 3px;
            color: #91a2bc;
            font-size: 13px;
            font-weight: 500;
        }
    }

    &__avatar-fallback {
        display: grid;
        place-items: center;
        background: #e8e7ff;
        color: #5149e8;
        font-size: 13px;
        font-weight: 800;
    }

    &__menu {
        display: inline-grid;
        place-items: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: #94a6c0;
        cursor: pointer;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.65;
        }

        .material-symbols-outlined {
            font-size: 20px;
        }
    }

    &__edit {
        position: relative;
        margin-top: 16px;
        border: 1px solid #dce5f5;
        border-radius: 8px;
        background: #f8faff;

        &:focus-within {
            border-color: #625bf6;
            box-shadow: 0 0 0 3px rgba(98, 91, 246, 0.12);
        }
    }

    &__edit-toolbar {
        display: flex;
        align-items: center;
        gap: 4px;
        min-height: 42px;
        padding: 6px 8px;
        border-bottom: 1px solid #dce5f5;

        button {
            display: inline-grid;
            place-items: center;
            width: 30px;
            height: 30px;
            padding: 0;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: #667892;
            cursor: pointer;

            &:hover,
            &.is-active {
                background: #e8e7ff;
                color: #5149e8;
            }
        }

        .material-symbols-outlined {
            font-size: 19px;
        }
    }

    &__edit-emoji-wrapper {
        position: relative;
    }

    &__edit-emoji-menu {
        position: absolute;
        top: 36px;
        right: 0;
        z-index: 5;
        display: grid;
        grid-template-columns: repeat(3, 36px);
        gap: 4px;
        padding: 8px;
        border: 1px solid #dce5f5;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0 10px 24px rgba(34, 48, 73, 0.14);

        button {
            width: 36px;
            height: 36px;
            font-size: 20px;
        }
    }

    :deep(.mural-post__edit-content) {
        min-height: 112px;
        padding: 16px;
        outline: none;
        color: #2f3b52;
        font-size: 15px;
        line-height: 1.55;

        p {
            margin: 0 0 8px;
        }

        p:last-child {
            margin-bottom: 0;
        }

        ul,
        ol {
            margin: 8px 0;
            padding-left: 24px;
        }
    }

    &__edit-attachment {
        padding: 0 12px 12px;

        > img {
            display: block;
            width: 100%;
            max-height: 360px;
            border-radius: 8px;
            object-fit: contain;
            background: #eef2f9;
        }

        &-actions {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 8px;

            button {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                min-height: 32px;
                padding: 0 10px;
                border: 1px solid #dce5f5;
                border-radius: 8px;
                background: #fff;
                color: #5149e8;
                font-size: 12px;
                font-weight: 700;
                cursor: pointer;

                &:disabled {
                    cursor: not-allowed;
                    opacity: 0.65;
                }
            }

            .mural-post__edit-remove-image {
                width: 32px;
                min-width: 32px;
                padding: 0;
                color: #dc2626;
            }
        }

        &-name {
            flex: 1 1 160px;
            overflow: hidden;
            min-width: 0;
            margin-right: auto;
            color: #667892;
            font-size: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__edit-add-image {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-height: 36px;
        margin: 0 12px 12px;
        padding: 0 12px;
        border: 1px solid #dce5f5;
        border-radius: 8px;
        background: #fff;
        color: #5149e8;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.65;
        }
    }

    &__edit-file-input {
        display: none;
    }

    &__edit-error {
        margin: 0;
        padding: 0 12px 10px;
        color: #dc2626;
        font-size: 12px;
    }

    &__edit-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 10px;
        border-top: 1px solid #dce5f5;

        button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            min-width: 88px;
            height: 34px;
            padding: 0 14px;
            border: 0;
            border-radius: 8px;
            background: transparent;
            color: #667892;
            font-weight: 700;
            cursor: pointer;

            &:disabled {
                cursor: not-allowed;
                opacity: 0.65;
            }
        }

        .mural-post__save {
            background: #625bf6;
            color: #fff;
        }
    }

    &__body {
        margin-top: 16px;
        color: #0f1621;
        font-size: 15px;
        line-height: 1.55;

        :deep(p) {
            margin: 0 0 10px;
        }

        :deep(p:last-child) {
            margin-bottom: 0;
        }

        :deep(ul),
        :deep(ol) {
            margin: 10px 0;
            padding-left: 24px;
        }

        :deep(a) {
            color: #5149e8;
            text-decoration: underline;
        }
    }

    &__attachment {
        margin-top: 16px;

        img {
            display: block;
            width: 100%;
            max-height: 520px;
            border-radius: 8px;
            object-fit: contain;
            background: #f8faff;
        }

        &-loading {
            display: flex;
            align-items: center;
            gap: 8px;
            min-height: 42px;
            padding: 8px 10px;
            border: 1px solid #dce5f5;
            border-radius: 8px;
            background: #f8faff;
            color: #5149e8;
            font-size: 13px;
            font-weight: 700;
        }

        &-loading {
            margin-bottom: 8px;
            color: #7d8ca4;
        }
    }
}

.dark-theme {
    .mural-post {
        border-color: var(--borderColor);
        background: #0c1427;
        box-shadow: none;

        &__author h3,
        &__body {
            color: var(--whiteColor);
        }

        &__author span:not(.mural-post__avatar-fallback),
        &__menu {
            color: var(--bodyColor);
        }

        &__avatar-fallback {
            background: rgba(98, 91, 246, 0.2);
            color: #aaa6ff;
        }

        &__attachment {
            img,
            &-loading {
                border-color: var(--borderColor);
                background: #15203c;
            }
        }

        &__edit {
            border-color: var(--borderColor);
            background: #15203c;
        }

        &__edit-toolbar,
        &__edit-actions {
            border-color: var(--borderColor);
        }

        &__edit-emoji-menu {
            border-color: var(--borderColor);
            background: #15203c;
        }

        &__edit-toolbar button,
        &__edit-actions button {
            color: var(--bodyColor);

            &:hover,
            &.is-active {
                background: rgba(98, 91, 246, 0.2);
                color: #aaa6ff;
            }
        }

        :deep(.mural-post__edit-content) {
            color: var(--whiteColor);
        }

        &__edit-attachment {
            > img {
                background: #0c1427;
            }

            &-actions button {
                border-color: var(--borderColor);
                background: #0c1427;
                color: var(--bodyColor);
            }

            &-name {
                color: var(--bodyColor);
            }

            &-actions .mural-post__edit-remove-image {
                color: #ff8b8b;
            }
        }

        &__edit-add-image {
            border-color: var(--borderColor);
            background: #0c1427;
            color: #aaa6ff;
        }

        &__edit-actions .mural-post__save {
            background: #625bf6;
            color: #fff;
        }
    }
}

.mural-post__menu-list {
    min-width: 150px;

    .material-symbols-outlined {
        margin-right: 10px;
        font-size: 19px;
    }
}

.mural-post__delete-option {
    color: #dc2626;
}

@media (max-width: 575px) {
    .mural-post {
        padding: 16px;
    }
}
</style>
